/**
 * Transport abstraction.
 *
 * The mesh runs over multiple transports concurrently. A per-conversation
 * sensitivity tag selects which transport(s) carry which traffic:
 *
 *   - 'low'      → libp2p direct (WebRTC / WebSockets), lowest latency
 *   - 'standard' → Waku mesh, mid-latency, metadata-private
 *   - 'high'     → Waku-over-Tor, slowest, strongest anonymity
 *   - 'offline'  → BLE (Bluetooth Low Energy) mesh + sneakernet fallback
 *
 * The transport layer is content-agnostic — it carries opaque envelope bytes.
 * Routing decisions never depend on payload inspection.
 */

import type { SignedEnvelope } from '../envelope/Envelope';

export type Sensitivity = 'low' | 'standard' | 'high' | 'offline';

export interface PeerId {
  readonly id: string;
  /** Libp2p multiaddr or Waku topic, depending on transport. */
  readonly address: string;
}

export interface Transport {
  readonly kind: 'libp2p' | 'waku' | 'tor' | 'ble' | 'mock';
  readonly available: () => Promise<boolean>;
  readonly send: (peer: PeerId, payload: SignedEnvelope) => Promise<void>;
  readonly broadcast: (topic: string, payload: SignedEnvelope) => Promise<void>;
  readonly subscribe: (topic: string, onMessage: (e: SignedEnvelope) => void) => () => void;
  readonly peers: () => Promise<PeerId[]>;
}

// ─────────────────────────────────────────────────────────────────────────────
// SELECTOR
// ─────────────────────────────────────────────────────────────────────────────

export class TransportSelector {
  #transports = new Map<Transport['kind'], Transport>();

  register(transport: Transport): void {
    this.#transports.set(transport.kind, transport);
  }

  /**
   * Choose a transport based on sensitivity AND availability.
   * Falls through to lower-privacy options if the preferred is unavailable.
   * Returns null if nothing is available (full network partition).
   */
  async select(sensitivity: Sensitivity): Promise<Transport | null> {
    const order: Transport['kind'][] = (() => {
      switch (sensitivity) {
        case 'high':     return ['tor', 'waku', 'libp2p', 'ble'];
        case 'standard': return ['waku', 'libp2p', 'tor', 'ble'];
        case 'low':      return ['libp2p', 'waku', 'ble'];
        case 'offline':  return ['ble'];
      }
    })();

    for (const kind of order) {
      const t = this.#transports.get(kind);
      if (t && (await t.available())) return t;
    }
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MOCK TRANSPORT for development
// ─────────────────────────────────────────────────────────────────────────────

export class MockTransport implements Transport {
  readonly kind = 'mock' as const;
  #topics = new Map<string, Set<(e: SignedEnvelope) => void>>();

  async available(): Promise<boolean> { return true; }

  async send(_peer: PeerId, _payload: SignedEnvelope): Promise<void> {
    // No-op
  }

  async broadcast(topic: string, payload: SignedEnvelope): Promise<void> {
    this.#topics.get(topic)?.forEach((cb) => cb(payload));
  }

  subscribe(topic: string, onMessage: (e: SignedEnvelope) => void): () => void {
    if (!this.#topics.has(topic)) this.#topics.set(topic, new Set());
    const subs = this.#topics.get(topic)!;
    subs.add(onMessage);
    return () => subs.delete(onMessage);
  }

  async peers(): Promise<PeerId[]> { return []; }
}

// TODO(beta): WakuTransport — wrap @waku/sdk with envelope marshalling
// TODO(beta): Libp2pTransport — wrap libp2p Node with pubsub topic mapping
// TODO(beta): TorTransport — proxy through onion service
// TODO(beta): BleTransport — Web Bluetooth mesh, GATT-based envelope chunking
